import { CSSProperties, FC, useState } from 'react';
import {
  addCardErrorMessage,
  addCardFailureVariant,
  addCardFieldErrors,
  type NewCardFieldErrors,
} from '../../application/addCardErrors';
import type { NewCardField } from '../../domain/errors';
import type { NewCardData } from '../../domain/models';
import { translate } from '../../i18n';
import { CardNumberInput } from '../CardNumberInput';
import { CardStatusModal, type CardStatusModalVariant } from '../CardStatusModal';
import { CardPreview } from '../CardPreview';
import { addCardFormStyles } from './styles';

export interface AddCardFormProps {
  onBack?: () => void;
  /**
   * Tokeniza y guarda la tarjeta. Si rechaza, el error se muestra en el formulario; si resuelve,
   * el contenedor cambia de vista. Los datos de la tarjeta solo viven en el estado de este form.
   */
  onSave?: (data: NewCardData) => Promise<void>;
  testID?: string;
  /** `page` es el modo app. `inline` es el alta de escritorio. `sheet` es el panel mobile. */
  variant?: 'page' | 'inline' | 'sheet';
}

const formatPan = (digits: string): string => digits.match(/.{1,4}/g)?.join(' ') ?? '';

const formatExpiryInput = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

const FormLevelNotice: FC<{ message: string; testID: string }> = ({ message, testID }) => (
  <div style={addCardFormStyles.formNotice} data-testid={testID} role="alert">
    <p style={addCardFormStyles.formNoticeText}>{message}</p>
  </div>
);

const FieldErrorMessage: FC<{ message: string; id: string }> = ({ message, id }) => (
  <p id={id} data-testid={id} style={addCardFormStyles.fieldErrorText} role="alert">
    {message}
  </p>
);

/**
 * AddCardForm — Pantalla "Agregar método de pago".
 *
 * Reúne los datos de la tarjeta y los entrega a `onSave`, que los tokeniza con el SDK de Payway
 * (ENA-1607). "Guardar tarjeta" se habilita con número, vencimiento, CVV, titular y DNI completos.
 * Tras guardar se vacían los campos sensibles.
 */
export const AddCardForm: FC<AddCardFormProps> = ({
  onBack,
  onSave,
  testID = 'add-card-form',
  variant = 'page',
}) => {
  const [cardNumber, setCardNumber] = useState('');
  const [expiryMonth, setExpiryMonth] = useState('');
  const [expiryYear, setExpiryYear] = useState('');
  const [cvv, setCvv] = useState('');
  const [holderName, setHolderName] = useState('');
  const [dni, setDni] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<NewCardFieldErrors>({});
  const [formError, setFormError] = useState<string | undefined>();
  /** Modal de estado ("Tarjeta inválida" / "Tarjeta expirada"); al cerrarlo se vuelve a editar los datos. */
  const [statusModal, setStatusModal] = useState<CardStatusModalVariant | null>(null);

  const clearFieldError = (field: NewCardField) => {
    setFieldErrors((prev) => {
      if (!prev[field]) {
        return prev;
      }
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const inputStyle = (base: CSSProperties, field: NewCardField): CSSProperties => ({
    ...base,
    ...(fieldErrors[field] ? addCardFormStyles.inputError : {}),
  });

  const canSave =
    cardNumber.length >= 15 &&
    expiryMonth !== '' &&
    expiryYear !== '' &&
    cvv.length >= 3 &&
    holderName.trim().length > 0 &&
    dni.trim().length > 0 &&
    !submitting;

  const clearSensitiveFields = () => {
    setCardNumber('');
    setCvv('');
    setExpiryMonth('');
    setExpiryYear('');
  };

  const handleSave = async () => {
    if (!canSave || !onSave) {
      return;
    }
    setFieldErrors({});
    setFormError(undefined);
    setSubmitting(true);
    try {
      await onSave({
        cardNumber,
        expirationMonth: expiryMonth,
        expirationYear: expiryYear,
        securityCode: cvv,
        holderName,
        documentNumber: dni,
      });
      clearSensitiveFields();
    } catch (saveError) {
      const variant = addCardFailureVariant(saveError);
      if (variant) {
        setStatusModal(variant);
        setFieldErrors({});
        setFormError(undefined);
      } else {
        const fields = addCardFieldErrors(saveError);
        if (Object.keys(fields).length > 0) {
          setFieldErrors(fields);
          setFormError(undefined);
        } else {
          setFieldErrors({});
          setFormError(addCardErrorMessage(saveError));
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (variant === 'inline' || variant === 'sheet') {
    const sheet = variant === 'sheet';
    const expiryValue = expiryMonth || expiryYear ? `${expiryMonth}${expiryYear ? `/${expiryYear}` : ''}` : '';
    return (
      <div className="web-add-card" style={sheet ? addCardFormStyles.sheetCard : addCardFormStyles.inlineCard} data-testid={testID}>
        <div style={sheet ? addCardFormStyles.sheetHeader : addCardFormStyles.inlineHeader}>
          <h2 style={addCardFormStyles.inlineTitle}>{translate('paymentsUI.web.newCardTitle')}</h2>
          <button
            type="button"
            onClick={onBack}
            aria-label={translate('paymentsUI.web.newCardCancel')}
            style={sheet ? addCardFormStyles.sheetClose : addCardFormStyles.inlineCancel}
          >
            {sheet ? null : translate('paymentsUI.web.newCardCancel')}
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
              <path d="M12.2 3.8a.75.75 0 0 1 0 1.06L9.06 8l3.14 3.14a.75.75 0 1 1-1.06 1.06L8 9.06l-3.14 3.14a.75.75 0 0 1-1.06-1.06L6.94 8 3.8 4.86a.75.75 0 1 1 1.06-1.06L8 6.94l3.14-3.14a.75.75 0 0 1 1.06 0Z" fill="currentColor" />
            </svg>
          </button>
        </div>
        <div className="web-add-card-columns" style={sheet ? addCardFormStyles.sheetColumns : addCardFormStyles.inlineColumns}>
          <CardPreview
            cardNumber={cardNumber}
            holderName={holderName}
            expiryMonth={expiryMonth}
            expiryYear={expiryYear}
            testID={`${testID}-preview`}
          />
          <div style={sheet ? addCardFormStyles.sheetFields : addCardFormStyles.inlineFields}>
            <div style={addCardFormStyles.fieldGroup}>
              <label style={addCardFormStyles.webLabel} htmlFor={`${testID}-card-number`}>
                {translate('paymentsUI.web.newCardNumber')}
              </label>
              <input
                id={`${testID}-card-number`}
                type="text"
                inputMode="numeric"
                autoComplete="cc-number"
                placeholder={translate('paymentsUI.web.newCardNumberPlaceholder')}
                value={formatPan(cardNumber)}
                onChange={(e) => {
                  clearFieldError('cardNumber');
                  setCardNumber(e.target.value.replace(/\D/g, '').slice(0, 16));
                }}
                data-testid={`${testID}-card-number`}
                style={inputStyle(addCardFormStyles.webInput, 'cardNumber')}
                aria-invalid={Boolean(fieldErrors.cardNumber)}
                aria-describedby={fieldErrors.cardNumber ? `${testID}-card-number-error` : undefined}
              />
              {fieldErrors.cardNumber && (
                <FieldErrorMessage id={`${testID}-card-number-error`} message={fieldErrors.cardNumber} />
              )}
            </div>
            <div style={addCardFormStyles.fieldGroup}>
              <label style={addCardFormStyles.webLabel} htmlFor={`${testID}-name`}>
                {translate('paymentsUI.web.newCardName')}
              </label>
              <input
                id={`${testID}-name`}
                type="text"
                autoComplete="cc-name"
                placeholder={translate('paymentsUI.web.newCardNamePlaceholder')}
                value={holderName}
                onChange={(e) => {
                  clearFieldError('holderName');
                  setHolderName(e.target.value);
                }}
                data-testid={`${testID}-name`}
                style={inputStyle(addCardFormStyles.webInput, 'holderName')}
                aria-invalid={Boolean(fieldErrors.holderName)}
                aria-describedby={fieldErrors.holderName ? `${testID}-name-error` : undefined}
              />
              {fieldErrors.holderName && (
                <FieldErrorMessage id={`${testID}-name-error`} message={fieldErrors.holderName} />
              )}
            </div>
            <div style={addCardFormStyles.expiryAndCvvRow}>
              <div style={addCardFormStyles.expiryGroup}>
                <label style={addCardFormStyles.webLabel} htmlFor={`${testID}-expiry`}>
                  {translate('paymentsUI.web.newCardExpiry')}
                </label>
                <input
                  id={`${testID}-expiry`}
                  type="text"
                  inputMode="numeric"
                  autoComplete="cc-exp"
                  placeholder={translate('paymentsUI.web.newCardExpiryPlaceholder')}
                  value={expiryValue}
                  onChange={(e) => {
                    clearFieldError('expiration');
                    const formatted = formatExpiryInput(e.target.value);
                    const [month = '', year = ''] = formatted.split('/');
                    setExpiryMonth(month);
                    setExpiryYear(year);
                  }}
                  data-testid={`${testID}-expiry`}
                  style={inputStyle(addCardFormStyles.webInput, 'expiration')}
                  aria-invalid={Boolean(fieldErrors.expiration)}
                  aria-describedby={fieldErrors.expiration ? `${testID}-expiry-error` : undefined}
                />
                {fieldErrors.expiration && (
                  <FieldErrorMessage id={`${testID}-expiry-error`} message={fieldErrors.expiration} />
                )}
              </div>
              <div style={{ ...addCardFormStyles.fieldGroup, flex: 1 }}>
                <label style={addCardFormStyles.webLabel} htmlFor={`${testID}-cvv`}>
                  {translate('paymentsUI.web.newCardSecurity')}
                </label>
                <input
                  id={`${testID}-cvv`}
                  type="text"
                  inputMode="numeric"
                  autoComplete="cc-csc"
                  placeholder={translate('paymentsUI.web.newCardSecurityPlaceholder')}
                  value={cvv}
                  onChange={(e) => {
                    clearFieldError('securityCode');
                    setCvv(e.target.value.replace(/\D/g, '').slice(0, 4));
                  }}
                  data-testid={`${testID}-cvv`}
                  style={inputStyle(addCardFormStyles.webInput, 'securityCode')}
                  aria-invalid={Boolean(fieldErrors.securityCode)}
                  aria-describedby={fieldErrors.securityCode ? `${testID}-cvv-error` : undefined}
                />
                {fieldErrors.securityCode && (
                  <FieldErrorMessage id={`${testID}-cvv-error`} message={fieldErrors.securityCode} />
                )}
              </div>
            </div>
            <div style={addCardFormStyles.fieldGroup}>
              <label style={addCardFormStyles.webLabel} htmlFor={`${testID}-dni`}>
                {translate('paymentsUI.web.newCardDni')}
              </label>
              <input
                id={`${testID}-dni`}
                type="text"
                inputMode="numeric"
                placeholder={translate('paymentsUI.web.newCardDniPlaceholder')}
                value={dni}
                onChange={(e) => {
                  clearFieldError('document');
                  setDni(e.target.value.replace(/\D/g, '').slice(0, 8));
                }}
                data-testid={`${testID}-dni`}
                style={inputStyle(addCardFormStyles.webInput, 'document')}
                aria-invalid={Boolean(fieldErrors.document)}
                aria-describedby={fieldErrors.document ? `${testID}-dni-error` : undefined}
              />
              {fieldErrors.document && (
                <FieldErrorMessage id={`${testID}-dni-error`} message={fieldErrors.document} />
              )}
            </div>
            {formError && <FormLevelNotice message={formError} testID={`${testID}-error`} />}
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={!canSave}
              data-testid={`${testID}-save`}
              style={{
                ...addCardFormStyles.inlineSave,
                ...(!canSave && addCardFormStyles.saveButtonMuted),
              }}
            >
              {translate(sheet ? 'paymentsUI.web.newCardSaveSheet' : 'paymentsUI.web.newCardSave')}
            </button>
          </div>
        </div>
        {statusModal && (
          <CardStatusModal
            variant={statusModal}
            onClose={() => setStatusModal(null)}
            testID={`${testID}-status-modal`}
          />
        )}
      </div>
    );
  }

  return (
    <div style={addCardFormStyles.container} data-testid={testID}>
      <div style={addCardFormStyles.body}>
        <div style={addCardFormStyles.header}>
          <button
            type="button"
            onClick={onBack}
            data-testid={`${testID}-back`}
            style={addCardFormStyles.backButton}
          >
            {translate('paymentsUI.addCardForm.back')}
          </button>
          <h2 style={addCardFormStyles.title}>{translate('paymentsUI.addCardForm.title')}</h2>
        </div>

        <CardPreview
          cardNumber={cardNumber}
          holderName={holderName}
          expiryMonth={expiryMonth}
          expiryYear={expiryYear}
          testID={`${testID}-preview`}
        />

      <CardNumberInput
        value={cardNumber}
        onChange={(value) => {
          clearFieldError('cardNumber');
          setCardNumber(value);
        }}
        label={translate('paymentsUI.cardNumberInput.label')}
        testID={`${testID}-card-number`}
      />
      {fieldErrors.cardNumber && (
        <FieldErrorMessage id={`${testID}-card-number-error`} message={fieldErrors.cardNumber} />
      )}

      <div style={addCardFormStyles.fieldGroup}>
        <label style={addCardFormStyles.fieldLabel} htmlFor={`${testID}-name`}>
          {translate('paymentsUI.addCardForm.nameLabel')}
        </label>
        <input
          id={`${testID}-name`}
          type="text"
          placeholder={translate('paymentsUI.addCardForm.namePlaceholder')}
          value={holderName}
          onChange={(e) => {
            clearFieldError('holderName');
            setHolderName(e.target.value);
          }}
          data-testid={`${testID}-name`}
          style={inputStyle(addCardFormStyles.textInput, 'holderName')}
          aria-invalid={Boolean(fieldErrors.holderName)}
          aria-describedby={fieldErrors.holderName ? `${testID}-name-error` : undefined}
        />
        {fieldErrors.holderName && (
          <FieldErrorMessage id={`${testID}-name-error`} message={fieldErrors.holderName} />
        )}
      </div>

      <div style={addCardFormStyles.expiryAndCvvRow}>
        <div style={addCardFormStyles.expiryGroup}>
          <label style={addCardFormStyles.fieldLabel} htmlFor={`${testID}-expiry`}>
            {translate('paymentsUI.addCardForm.expiryLabel')}
          </label>
          <input
            id={`${testID}-expiry`}
            type="text"
            inputMode="numeric"
            autoComplete="cc-exp"
            placeholder={translate('paymentsUI.addCardForm.expiryPlaceholder')}
            value={expiryMonth || expiryYear ? `${expiryMonth}${expiryYear ? `/${expiryYear}` : ''}` : ''}
            onChange={(e) => {
              clearFieldError('expiration');
              const formatted = formatExpiryInput(e.target.value);
              const [month = '', year = ''] = formatted.split('/');
              setExpiryMonth(month);
              setExpiryYear(year);
            }}
            data-testid={`${testID}-expiry`}
            style={inputStyle(addCardFormStyles.textInput, 'expiration')}
            aria-invalid={Boolean(fieldErrors.expiration)}
            aria-describedby={fieldErrors.expiration ? `${testID}-expiry-error` : undefined}
          />
          {fieldErrors.expiration && (
            <FieldErrorMessage id={`${testID}-expiry-error`} message={fieldErrors.expiration} />
          )}
        </div>

        <div style={addCardFormStyles.cvvGroup}>
          <label style={addCardFormStyles.fieldLabel} htmlFor={`${testID}-cvv`}>
            {translate('paymentsUI.addCardForm.securityLabel')}
          </label>
          <input
            id={`${testID}-cvv`}
            type="text"
            inputMode="numeric"
            autoComplete="cc-csc"
            placeholder={translate('paymentsUI.addCardForm.securityPlaceholder')}
            value={cvv}
            onChange={(e) => {
              clearFieldError('securityCode');
              setCvv(e.target.value.replace(/\D/g, '').slice(0, 4));
            }}
            data-testid={`${testID}-cvv`}
            style={inputStyle(addCardFormStyles.textInput, 'securityCode')}
            aria-invalid={Boolean(fieldErrors.securityCode)}
            aria-describedby={fieldErrors.securityCode ? `${testID}-cvv-error` : undefined}
          />
          {fieldErrors.securityCode && (
            <FieldErrorMessage id={`${testID}-cvv-error`} message={fieldErrors.securityCode} />
          )}
        </div>
      </div>

      <div style={addCardFormStyles.fieldGroup}>
        <label style={addCardFormStyles.fieldLabel} htmlFor={`${testID}-dni`}>
          {translate('paymentsUI.addCardForm.dniLabel')}
        </label>
        <input
          id={`${testID}-dni`}
          type="text"
          inputMode="numeric"
          placeholder={translate('paymentsUI.addCardForm.dniPlaceholder')}
          value={dni}
          onChange={(e) => {
            clearFieldError('document');
            setDni(e.target.value);
          }}
          data-testid={`${testID}-dni`}
          style={inputStyle(addCardFormStyles.textInput, 'document')}
          aria-invalid={Boolean(fieldErrors.document)}
          aria-describedby={fieldErrors.document ? `${testID}-dni-error` : undefined}
        />
        {fieldErrors.document && (
          <FieldErrorMessage id={`${testID}-dni-error`} message={fieldErrors.document} />
        )}
      </div>

      {formError && <FormLevelNotice message={formError} testID={`${testID}-error`} />}
      </div>

      {statusModal && (
        <CardStatusModal
          variant={statusModal}
          onClose={() => setStatusModal(null)}
          testID={`${testID}-status-modal`}
        />
      )}

      <div style={addCardFormStyles.actions}>
        <button
          type="button"
          onClick={onBack}
          data-testid={`${testID}-cancel`}
          style={addCardFormStyles.cancelButton}
        >
          {translate('paymentsUI.common.cancel')}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          data-testid={`${testID}-save`}
          style={{
            ...addCardFormStyles.saveButton,
            ...(!canSave && addCardFormStyles.saveButtonMuted),
          }}
        >
          {translate(submitting ? 'paymentsUI.addCardForm.saving' : 'paymentsUI.addCardForm.save')}
        </button>
      </div>
    </div>
  );
};
